<script lang="ts">
  import type { PwNextTableProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { Text } from '@squareup/public-web-design-system';

  const componentName = 'PwNextTable';

  // #region Props
  type $$Props = PwNextTableProps;
  export let entryId: PwNextTableProps['entryId'];
  export let heading: PwNextTableProps['heading'];
  export let table: PwNextTableProps['table'];
  export let headingOptions: PwNextTableProps['headingOptions'];
  // #endregion

  // #region Setup
  const isFirstHeaderEmpty = table.rows[0]?.length > table.header.length;
  // #endregion
</script>

<div class="{componentName} {componentName}-{entryId}-box-properties" data-entry-id={entryId}>
  <div class="{componentName}-wrapper">
    {#if heading?.length > 0}
      <div class="{componentName}-heading">
        <Text variant={headingOptions.variant} as={headingOptions.as} isBalanced={headingOptions.isBalanced}>
          {heading}
        </Text>
      </div>
    {/if}
    <div class="{componentName}-table" style:--grid-columns={table.rows[0]?.length} role="table">
      <div class="{componentName}-table-header {componentName}-table-row desktop" role="row">
        <!-- Add an empty space above the first column -->
        {#if isFirstHeaderEmpty}
          <div class="{componentName}-table-header-cell {componentName}-table-cell empty" />
        {/if}
        {#each table.header as header}
          <div class="{componentName}-table-header-cell {componentName}-table-cell" role="columnheader">
            <DynamicChildrenComponent children={[header]} />
          </div>
        {/each}
      </div>
      {#each table.rows as row}
        <div class="{componentName}-table-row" role="row">
          {#each row as cell, i}
            <div class="{componentName}-table-cell" role={i === 0 ? 'rowheader' : 'cell'}>
              {#if i > 0}
                <div class="{componentName}-table-header-cell mobile">
                  <DynamicChildrenComponent children={[table.header[isFirstHeaderEmpty ? i - 1 : i]]} />
                </div>
              {/if}
              <DynamicChildrenComponent children={[cell]} />
            </div>
          {/each}
        </div>
      {/each}
    </div>
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as media;

  @layer custom-components {
    .PwNextTable {
      display: flex;
      justify-content: center;

      &-wrapper {
        width: var(--container-width-current);
      }

      &-heading {
        margin-bottom: var(--space-100);
        text-align: center;
      }

      &-table {
        display: grid;
        grid-row-gap: var(--space-20);

        @include media.at('small') {
          grid-template-columns: repeat(var(--grid-columns, 1), 1fr);
          grid-row-gap: unset;
          text-align: center;
        }

        &-header {
          &.desktop {
            display: none;

            @include media.at('small') {
              display: contents;
            }
          }

          &-cell {
            &.mobile {
              @include media.at('small') {
                display: none;
              }
            }
          }
        }

        &-row {
          display: contents;
        }

        &-cell {
          display: flex;
          flex-direction: row;
          gap: var(--space-20);

          &:first-of-type {
            justify-content: flex-start;
            padding: var(--space-30) 0 var(--space-20);
            text-align: left;
            border-bottom: 1px solid var(--color-gray-15);
          }

          @include media.at('small') {
            justify-content: center;
            border-bottom: 1px solid var(--color-gray-15);
            &,
            &:first-of-type {
              padding: var(--space-40) 0;
            }
          }

          &:not(:first-child) {
            :global {
              & > * {
                width: 50%;
                @include media.at('small') {
                  width: unset;
                }
              }
            }
          }
        }
      }
    }
  }
</style>
